import HttpClient from '@/api/HttpClient';

const httpClient = new HttpClient();

export type TomVendingSiteVisitHistoryListRow = {
  id: number;
  visitDate: string;
  userId: number;
  userDisplayName: string | null;
  totalCashCollectedCents?: number;
};

export type TomVendingSiteVisitHistoryDetailLine = {
  inventoryItemId: number;
  upc: string;
  itemName: string | null;
  parQty: number;
  fillQty: number;
  rejectQty: number;
  recycleQty: number;
  endingQty: number;
};

export type TomVendingSiteVisitHistoryMachine = {
  vendingMachineId: number;
  assetId: string | null;
  appliedAt: string;
  lines: TomVendingSiteVisitHistoryDetailLine[];
};

export type TomVendingSiteVisitHistoryRecentRow = {
  siteVisitId: number;
  locationTomVendingId: number;
  locationName: string;
  visitDate: string;
  userDisplayName: string | null;
  machineSnapshotCount: number;
  totalCashCollectedCents?: number;
};

export type TomVendingSiteVisitCashCollection = {
  vendingMachineId: number | null;
  assetId: string | null;
  billsCents: number;
  coinsCents: number;
  totalCents: number;
  notes: string | null;
  appliedAt: string;
};

export async function fetchTomVendingSiteVisitHistoryList(params: {
  locationTomVendingId: number;
  skip?: number;
  take?: number;
}): Promise<{ data: TomVendingSiteVisitHistoryListRow[]; total: number }> {
  const response = await httpClient.post('api/tom-vending/site-visit/history/list', {
    locationTomVendingId: params.locationTomVendingId,
    skip: params.skip ?? 0,
    take: params.take ?? 50,
  });
  const d = response.data as { data?: TomVendingSiteVisitHistoryListRow[]; total?: number };
  return { data: d.data ?? [], total: d.total ?? 0 };
}

export async function fetchTomVendingSiteVisitHistoryDetail(params: {
  siteVisitId: number;
  locationTomVendingId?: number;
  vendingMachineId?: number;
}): Promise<{
  siteVisitId: number;
  visitDate: string;
  locationTomVendingId: number;
  locationName: string;
  userDisplayName: string | null;
  machines: TomVendingSiteVisitHistoryMachine[];
  cashCollections: TomVendingSiteVisitCashCollection[];
  siteCashTotalCents: number;
}> {
  const response = await httpClient.post('api/tom-vending/site-visit/history/snapshot-detail', {
    siteVisitId: params.siteVisitId,
    locationTomVendingId: params.locationTomVendingId,
    vendingMachineId: params.vendingMachineId,
  });
  return response.data as {
    siteVisitId: number;
    visitDate: string;
    locationTomVendingId: number;
    locationName: string;
    userDisplayName: string | null;
    machines: TomVendingSiteVisitHistoryMachine[];
    cashCollections: TomVendingSiteVisitCashCollection[];
    siteCashTotalCents: number;
  };
}

export async function fetchTomVendingSiteVisitHistoryByRange(params: {
  dateFrom: string;
  dateTo: string;
  locationTomVendingId?: number;
  skip?: number;
  take?: number;
}): Promise<{ data: TomVendingSiteVisitHistoryRecentRow[]; total: number }> {
  const response = await httpClient.post('api/tom-vending/site-visit/history/list-by-range', {
    dateFrom: params.dateFrom,
    dateTo: params.dateTo,
    locationTomVendingId: params.locationTomVendingId,
    skip: params.skip ?? 0,
    take: params.take ?? 50,
  });
  const d = response.data as { data?: TomVendingSiteVisitHistoryRecentRow[]; total?: number };
  return { data: d.data ?? [], total: d.total ?? 0 };
}

export async function fetchTomVendingSiteVisitHistoryRecent(params?: {
  skip?: number;
  take?: number;
}): Promise<{ data: TomVendingSiteVisitHistoryRecentRow[]; total: number }> {
  const response = await httpClient.post('api/tom-vending/site-visit/history/recent', {
    skip: params?.skip ?? 0,
    take: params?.take ?? 20,
  });
  const d = response.data as { data?: TomVendingSiteVisitHistoryRecentRow[]; total?: number };
  return { data: d.data ?? [], total: d.total ?? 0 };
}

export type TomVendingRejectReportRow = {
  source: 'site_visit' | 'warehouse';
  siteVisitId: number | null;
  visitDate: string;
  visitStatus: 'open' | 'finalized' | 'void' | null;
  locationTomVendingId: number;
  locationName: string;
  vendingMachineId: number | null;
  machineAssetId: string | null;
  inventoryItemId: number;
  upc: string;
  itemName: string | null;
  rejectQty: number;
  recordedByDisplayName: string | null;
  recordedAt: string;
};

export async function fetchTomVendingRejectReport(params: {
  dateFrom: string;
  dateTo: string;
  locationTomVendingId?: number;
  skip?: number;
  take?: number;
}): Promise<{ rows: TomVendingRejectReportRow[]; totalRejectUnits: number; total: number }> {
  const response = await httpClient.post('api/tom-vending/site-visit/reject-report', {
    dateFrom: params.dateFrom,
    dateTo: params.dateTo,
    locationTomVendingId: params.locationTomVendingId,
    skip: params.skip ?? 0,
    take: params.take ?? 50,
  });
  const d = response.data as {
    rows?: TomVendingRejectReportRow[];
    totalRejectUnits?: number;
    total?: number;
  };
  return {
    rows: d.rows ?? [],
    totalRejectUnits: d.totalRejectUnits ?? 0,
    total: d.total ?? 0,
  };
}

export async function finalizeStaleTomVendingSiteVisits(): Promise<{
  attempted: number;
  finalized: number;
  errors: string[];
}> {
  const response = await httpClient.post('api/tom-vending/site-visit/finalize-stale', {});
  return response.data as { attempted: number; finalized: number; errors: string[] };
}
